<template>
  <Modal ref="modalRef" @after-close="clearForm">
    <template #body>
      <View gap="xl" direction="column" class="modal-body">
        <View gap="md" direction="column" align="center" class="header">
          <Text variant="h2" align="center">Accept showing request with {{ name }}!</Text>

          <Text variant="body-large" align="center" color="secondary">
            Once accepted, showing details still can be updated from the showing page
          </Text>
        </View>

        <FormField label="Showing will be held by" :error="form.fieldErrors('showingAgentId')">
          <ShowingAgentPicker v-model="form.fields.showingAgentId" />
        </FormField>

        <Card size="medium" variant="outline">
          <View gap="md" direction="column">
            <Text variant="h4">What happens next?</Text>

            <View v-for="(item, index) in items" :key="item.title" gap="md" direction="row">
              <View align="center" justify="center" class="item-number">{{ index + 1 }}</View>
              <View gap="xs" direction="column">
                <Text variant="h4">{{ item.title }}</Text>
                <Text variant="body-medium" color="secondary">
                  {{ item.description }}
                </Text>
              </View>
            </View>
          </View>
        </Card>
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button :loading="isLoading" @click="accept">Accept</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { computed, ref } from 'vue';

import { Button, Card, FormField, Modal, Text, View, useAlertsStore, useForm } from '@tallo/design-system';
import { ShowingAgentPicker } from '@tallo/investor/showing-agents';

import { useAcceptShowingRequest } from '../../mutations/accept-showing-request.mutation';

const props = defineProps<{
  renterName: string;
  propertyId: string;
  showingRequestId: string;
  showingAgentId?: string;
}>();
const emit = defineEmits(['accepted']);

const { isLoading, mutateAsync: acceptShowing } = useAcceptShowingRequest();
const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const name = computed(() => props.renterName.split(' ')[0]);
const items = computed<{ title: string; description: string }[]>(() => [
  {
    title: 'Tallo will confirm the showing',
    description: 'Tallo will let renter know the showing has been accepted',
  },
  {
    title: 'Send reminders',
    description: 'Tallo will send reminders two hours before the showing to both parties',
  },
  {
    title: 'Answer questions',
    description: 'If the renter has any questions along the way, Tallo will handle them',
  },
]);

const form = useForm<{ showingAgentId: string }>(
  {
    showingAgentId: props.showingAgentId as string,
  },
  {
    showingAgentId: { required: helpers.withMessage('Showing agent is required', required) },
  },
);

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function clearForm() {
  form.fields.showingAgentId = '';
  form.resetValidation();
}

function accept() {
  form.submit(async () => {
    await acceptShowing({
      propertyId: props.propertyId,
      showingRequestId: props.showingRequestId,
      showingAgentId: form.fields.showingAgentId,
    });

    emit('accepted');
    close();
    alertsStore.success('Showing accepted');
  });
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.header {
  max-width: 30rem;
  margin: auto;
}

.modal-body {
  max-width: 37rem;
}

.item-number {
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  background-color: var(--t-color-neutral-200);
  border-radius: var(--t-border-radius-sm);
}

.actions > * {
  width: 100%;
}
</style>
